(() => {
  const EMPTY_VALUE = "__bh_oem_unspecified__";
  const SELECT_ORDER = [
    "make",
    "model",
    "generation",
    "engineName",
    "tcu",
  ];
  const URL_KEYS = {
    make: "oem_make",
    model: "oem_model",
    generation: "oem_generation",
    year: "oem_year",
    engineName: "oem_engine",
    tcu: "oem_tcu",
    application: "oem_application",
    option: "oem_option",
    packageType: "oem_package",
  };
  const PACKAGE_TYPES = [
    ["ecu", "priceEcu"],
    ["tcu", "priceTcu"],
    ["ecuTcuPackage", "pricePackage"],
  ];
  const ATTRIBUTION_FIELDS = [
    ["utmSource", "utm_source"],
    ["utmMedium", "utm_medium"],
    ["utmCampaign", "utm_campaign"],
    ["utmContent", "utm_content"],
    ["utmTerm", "utm_term"],
    ["placement", "placement"],
    ["fbclid", "fbclid"],
    ["landingPage", "landing_page"],
  ];
  const ATTRIBUTION_STORAGE_KEY = "boosthaus_oem_attribution";
  const SUCCESS_REDIRECT_PATH = "/pages/oem-thank-you?submitted=1";
  const DATA_REQUESTS = new Map();
  let confirmedSubmissionHandled = false;

  function requestData(dataUrl) {
    if (!DATA_REQUESTS.has(dataUrl)) {
      const request = fetch(dataUrl, {
        credentials: "same-origin",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `OEM+ data request failed with ${response.status}`,
            );
          }
          return response.json();
        })
        .catch((error) => {
          DATA_REQUESTS.delete(dataUrl);
          throw error;
        });
      DATA_REQUESTS.set(dataUrl, request);
    }

    return DATA_REQUESTS.get(dataUrl);
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) {
      element.className = className;
    }
    if (text !== undefined && text !== null) {
      element.textContent = String(text);
    }
    return element;
  }

  function uniqueSorted(values) {
    return [...new Set(values)].sort((left, right) =>
      String(left).localeCompare(String(right), "bg", {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }

  class BoostHausOemConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.data = null;
      this.loadingPromise = null;
      this.restoring = false;
      this.lastTrackedVehicle = "";
      this.lastTrackedResult = "";
      this.configuratorStarted = false;
      this.formStarted = false;
      this.formInView = false;
      this.stickyState = "";
      this.state = Object.fromEntries(SELECT_ORDER.map((key) => [key, ""]));
      this.selected = {
        applicationId: "",
        optionKey: "",
        packageType: "",
        priceKey: "",
      };
      this.labels = this.readLabels();
      this.showPrices = this.dataset.showPrices === "true";
      this.showOutputs = this.dataset.showOutputs === "true";
      this.showSource = this.dataset.showSource === "true";
      this.selects = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-select]")].map((select) => {
          select.dataset.placeholder =
            select.options[0]?.textContent ?? this.labels.choose;
          return [select.dataset.bhOemSelect, select];
        }),
      );
      this.fields = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-field]")].map((field) => [
          field.dataset.bhOemField,
          field,
        ]),
      );
      this.status = this.querySelector("[data-bh-oem-status]");
      this.statusText = this.querySelector("[data-bh-oem-status-text]");
      this.fieldsContainer = this.querySelector("[data-bh-oem-fields]");
      this.retryButton = this.querySelector("[data-bh-oem-retry]");
      this.clearButton = this.querySelector("[data-bh-oem-clear]");
      this.results = this.querySelector("[data-bh-oem-results]");
      this.resultsTitle = this.querySelector("[data-bh-oem-results-title]");
      this.resultsGrid = this.querySelector("[data-bh-oem-results-grid]");
      this.empty = this.querySelector("[data-bh-oem-empty]");
      this.emptyTitle = this.querySelector("[data-bh-oem-empty-title]");
      this.emptyText = this.querySelector("[data-bh-oem-empty-text]");
      this.summary = this.querySelector("[data-bh-oem-summary-value]");
      this.summary.dataset.defaultText = this.summary.textContent.trim();
      this.summaryDetails = this.querySelector(
        "[data-bh-oem-summary-details]",
      );
      this.summaryFields = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-summary-field]")].map(
          (field) => [field.dataset.bhOemSummaryField, field],
        ),
      );
      this.hiddenFields = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-hidden]")].map((input) => [
          input.dataset.bhOemHidden,
          input,
        ]),
      );
      this.progressItems = Object.fromEntries(
        [...this.querySelectorAll("[data-bh-oem-progress]")].map((item) => [
          item.dataset.bhOemProgress,
          item,
        ]),
      );
      this.loadFallback = this.querySelector("[data-bh-oem-load-fallback]");
      this.selectorSection = this.querySelector(".bh-oem-selector");
      this.heroTitle = this.querySelector(".bh-oem-hero__title");
      this.heroIntro = this.querySelector(".bh-oem-hero__text");
      this.initialTitle = this.heroTitle?.textContent.trim() || "";
      this.initialIntro = [...(this.heroIntro?.childNodes ?? [])].map((node) => node.cloneNode(true));
      this.contextWrap = this.querySelector("[data-bh-oem-context-wrap]");
      this.contextText = this.querySelector("[data-bh-oem-context]");
      this.contextEdit = this.querySelector("[data-bh-oem-context-edit]");
      this.doneEdit = this.querySelector("[data-bh-oem-done-edit]");
      this.contextEdit?.addEventListener("click", () => this.editSelection());
      this.doneEdit?.addEventListener("click", () => {
        this.dataset.editing = "false";
        this.contextEdit?.setAttribute("aria-expanded", "false");
        this.scrollToElement(this.results);
        this.querySelector('.bh-oem-option[aria-checked="true"]')?.focus({ preventScroll: true });
      });
      this.querySelector("[data-bh-oem-consult]")?.addEventListener("click", (event) => {
        event.preventDefault();
        this.scrollToForm();
      });
      this.formSection = this.querySelector(".bh-oem-lead");
      this.editButton = this.querySelector("[data-bh-oem-edit]");
      this.resultsEditButton = this.querySelector(
        "[data-bh-oem-edit-results]",
      );
      this.form = this.querySelector(".bh-oem-form");
      this.emailInput = this.querySelector('input[name="email"]');
      this.emailError = this.querySelector("[data-bh-oem-email-error]");
      this.formError = this.querySelector("[data-bh-oem-form-error]");
      this.formSubject = this.querySelector("[data-bh-oem-form-subject]");
      this.formSubjectDefault = this.formSubject?.value.trim() || "OEM+ enquiry";
      this.formSubmit = this.querySelector("[data-bh-oem-form-submit]");
      this.formSubmitDefaultText = this.formSubmit?.textContent.trim() || "";
      this.sticky = this.querySelector("[data-bh-oem-sticky]");
      this.stickySummary = this.querySelector(
        "[data-bh-oem-sticky-summary]",
      );
      this.stickyDetail = this.querySelector(
        "[data-bh-oem-sticky-detail]",
      );
      this.stickyAction = this.querySelector(
        "[data-bh-oem-sticky-action]",
      );
      if (this.editButton) {
        this.editButton.hidden = true;
      }

      for (const [key, select] of Object.entries(this.selects)) {
        select.addEventListener("pointerdown", () =>
          this.trackConfiguratorStart(),
        );
        select.addEventListener("keydown", () =>
          this.trackConfiguratorStart(),
        );
        select.addEventListener("change", () => {
          this.trackConfiguratorStart();
          this.handleChange(key);
        });
      }

      this.retryButton?.addEventListener("click", () =>
        this.loadData({ restore: this.hasRestorableUrlState() }),
      );
      this.clearButton?.addEventListener("click", () => this.clearSelection());
      this.editButton?.addEventListener("click", () => this.editSelection());
      this.resultsEditButton?.addEventListener("click", () =>
        this.editSelection(),
      );
      this.querySelectorAll("[data-bh-oem-primary]").forEach((control) => {
        control.addEventListener("click", (event) => {
          event.preventDefault();
          this.publish("boosthaus_oem_primary_offer_click", {
            event_source: "oem_configurator",
            cta_location: control.dataset.ctaLocation || "page",
            ...this.analyticsSelection(),
          });
          this.continueJourney();
        });
      });
      this.querySelectorAll("[data-bh-oem-manual]").forEach((control) => {
        control.addEventListener("click", () => this.selectManualInquiry());
      });
      this.querySelectorAll("[data-bh-oem-viber]").forEach((link) => {
        link.addEventListener("click", () => {
          this.publish("boosthaus_oem_viber_click", {
            event_source: "oem_configurator",
            cta_location: link.closest(".bh-oem-hero")
              ? "hero"
              : link.closest(".bh-oem-lead")
                ? "form"
                : "fallback",
            ...this.analyticsSelection(),
          });
        });
      });
      this.emailInput?.addEventListener("invalid", () =>
        this.showEmailError(),
      );
      this.emailInput?.addEventListener("input", () => {
        if (this.emailInput.validity.valid) {
          this.hideEmailError();
        }
      });
      this.form?.addEventListener("submit", (event) =>
        this.handleFormSubmit(event),
      );
      this.form?.addEventListener(
        "focusin",
        () => {
          if (this.formStarted) {
            return;
          }
          this.formStarted = true;
          this.publish("boosthaus_oem_enquiry_form_started", {
            event_source: "oem_configurator",
            ...this.analyticsSelection(),
          });
        },
        { once: true },
      );
      this.stickyAction?.addEventListener("click", () =>
        this.handleStickyAction(),
      );
      if ("IntersectionObserver" in window && this.formSection) {
        this.formVisibilityObserver = new IntersectionObserver(
          (entries) => {
            this.formInView = entries.some(
              (entry) => entry.isIntersecting && entry.intersectionRatio > 0.08,
            );
            this.updateStickyCta();
          },
          { threshold: [0, 0.08, 0.25] },
        );
        this.formVisibilityObserver.observe(this.formSection);
      }
      window.addEventListener("resize", () => this.updateStickyCta());
      this.captureAttribution();
      this.updateViberLinks();
      this.setStatus(this.labels.loading, "loading");
      this.updateProgress();
      this.publish("boosthaus_oem_page_viewed", {
        event_source: "oem_configurator",
        device_context: this.deviceContext(),
      });

      this.loadData({
        restore: this.hasRestorableUrlState(),
      });
    }

    readLabels() {
      return {
        personalTitle: this.dataset.labelPersonalTitle,
        personalSubtitle: this.dataset.labelPersonalSubtitle,
        stageHeading: this.dataset.labelStageHeading,
        stageReview: this.dataset.labelStageReview,
        configurationTitle: this.dataset.labelConfigurationTitle,
        includes: this.dataset.labelIncludes,
        packageHeading: this.dataset.labelPackageHeading,
        preliminaryPrice: this.dataset.labelPreliminaryPrice,
        power: this.dataset.labelPower,
        torque: this.dataset.labelTorque,
        before: this.dataset.labelBefore,
        technicalDetails: this.dataset.labelTechnicalDetails,
        extraOptions: this.dataset.labelExtraOptions,
        checkNote: this.dataset.labelCheckNote,
        tcuCheck: this.dataset.labelTcuCheck,
        opfCheck: this.dataset.labelOpfCheck,
        ecuIncluded: this.dataset.labelEcuIncluded,
        tcuIncluded: this.dataset.labelTcuIncluded,
        viberDiscuss: this.dataset.labelViberDiscuss,
        tcuOnlyNote: this.dataset.labelTcuOnlyNote,
        featureNote: this.dataset.labelFeatureNote,
        featurePriceNote: this.dataset.labelFeaturePriceNote,
        idle: this.dataset.labelIdle,
        loading: this.dataset.labelLoading,
        ready: this.dataset.labelReady,
        loadError: this.dataset.labelLoadError,
        retry: this.dataset.labelRetry,
        restored: this.dataset.labelRestored,
        restoreIgnored: this.dataset.labelRestoreIgnored,
        choose: this.dataset.labelChoose,
        unknown: this.dataset.labelUnknown,
        unspecifiedYear: this.dataset.labelUnspecifiedYear,
        unspecifiedGeneration: this.dataset.labelUnspecifiedGeneration,
        unspecifiedEngine: this.dataset.labelUnspecifiedEngine,
        uncertainTcu: this.dataset.labelUncertainTcu,
        resultSingular: this.dataset.labelResultSingular,
        resultPlural: this.dataset.labelResultPlural,
        resultsFoundSingle: this.dataset.labelResultsFoundSingle,
        resultsFoundPlural: this.dataset.labelResultsFoundPlural,
        fuel: this.dataset.labelFuel,
        tcu: this.dataset.labelTcu,
        engineCode: this.dataset.labelEngineCode,
        priceEcu: this.dataset.labelPriceEcu,
        priceTcu: this.dataset.labelPriceTcu,
        pricePackage: this.dataset.labelPricePackage,
        priceSelection: this.dataset.labelPriceSelection,
        packageConfirmation: this.dataset.labelPackageConfirmation,
        vatIncluded: this.dataset.labelVatIncluded,
        installationIncluded: this.dataset.labelInstallationIncluded,
        installationConfirm: this.dataset.labelInstallationConfirm,
        stock: this.dataset.labelStock,
        tuned: this.dataset.labelTuned,
        gain: this.dataset.labelGain,
        ron: this.dataset.labelRon,
        requiredHardware: this.dataset.labelRequiredHardware,
        recommendedHardware: this.dataset.labelRecommendedHardware,
        available: this.dataset.labelAvailable,
        noOptions: this.dataset.labelNoOptions,
        softwareOptions: this.dataset.labelSoftwareOptions,
        selectOption: this.dataset.labelSelectOption,
        selectPackage: this.dataset.labelSelectPackage,
        submitSelection: this.dataset.labelSubmitSelection,
        incompleteSelection: this.dataset.labelIncompleteSelection,
        inquireApplication: this.dataset.labelInquireApplication,
        source: this.dataset.labelSource,
        manualReview: this.dataset.labelManualReview,
        variant: this.dataset.labelVariant,
        noMatch: this.dataset.labelNoMatch,
        noMatchText: this.dataset.labelNoMatchText,
        petrol: this.dataset.labelPetrol,
        diesel: this.dataset.labelDiesel,
        yes: this.dataset.labelYes,
        no: this.dataset.labelNo,
        selected: this.dataset.labelSelected,
        packageEcuIncludes: this.dataset.labelPackageEcuIncludes,
        packageTcuIncludes: this.dataset.labelPackageTcuIncludes,
        packageCombinedIncludes: this.dataset.labelPackageCombinedIncludes,
        packageConfirmationIncludes:
          this.dataset.labelPackageConfirmationIncludes,
        packageEcuContext: this.dataset.labelPackageEcuContext,
        packageTcuContext: this.dataset.labelPackageTcuContext,
        packageCombinedContext: this.dataset.labelPackageCombinedContext,
        packageConfirmationContext:
          this.dataset.labelPackageConfirmationContext,
        hardwareNotIncluded: this.dataset.labelHardwareNotIncluded,
        emailError: this.dataset.labelEmailError,
        resultNextTitle: this.dataset.labelResultNextTitle,
        resultNextText: this.dataset.labelResultNextText,
        preliminaryOffer: this.dataset.labelPreliminaryOffer,
        offerPending: this.dataset.labelOfferPending,
        stickyOption: this.dataset.labelStickyOption,
        stickyPackage: this.dataset.labelStickyPackage,
        stickyOffer: this.dataset.labelStickyOffer,
        stickyManual: this.dataset.labelStickyManual,
        formSubmitting: this.dataset.labelFormSubmitting,
      };
    }

    trackConfiguratorStart() {
      if (this.configuratorStarted || !this.data) {
        return;
      }
      this.configuratorStarted = true;
      this.publish("boosthaus_oem_configurator_start", {
        event_source: "oem_configurator",
        device_context: this.deviceContext(),
      });
    }

    async loadData({ restore = false } = {}) {
      if (this.data) {
        if (restore) {
          this.restoreFromUrl();
        }
        return;
      }
      if (this.loadingPromise) {
        return this.loadingPromise;
      }

      if (this.retryButton) {
        this.retryButton.disabled = true;
        this.retryButton.hidden = true;
      }
      this.fieldsContainer?.setAttribute("aria-busy", "true");
      this.setStatus(this.labels.loading, "loading");

      this.loadingPromise = (async () => {
        try {
          const data = await requestData(this.dataset.dataUrl);
          this.validateData(data);
          this.data = data;
          this.setHiddenValue(
            "dataVersion",
            `${data.schemaVersion} / ${data.generatedDate}`,
          );
          this.populateSelect(
            "make",
            uniqueSorted(
              data.applications.map((application) => application.make),
            ),
          );
          this.selects.make.disabled = false;
          if (this.retryButton) {
            this.retryButton.hidden = true;
          }
          if (this.loadFallback) {
            this.loadFallback.hidden = true;
          }
          this.fieldsContainer?.setAttribute("aria-busy", "false");
          this.setStatus(this.labels.ready, "ready");
          this.updateViberLinks();
          this.updateStickyCta();

          if (restore) {
            this.restoreFromUrl();
          }
        } catch (error) {
          DATA_REQUESTS.delete(this.dataset.dataUrl);
          this.fieldsContainer?.setAttribute("aria-busy", "false");
          if (this.retryButton) {
            this.retryButton.disabled = false;
            this.retryButton.hidden = false;
            this.retryButton.textContent = this.labels.retry;
          }
          if (this.loadFallback) {
            this.loadFallback.hidden = false;
          }
          this.setStatus(this.labels.loadError, "error");
          this.publish("boosthaus_oem_data_error", {
            event_source: "oem_configurator",
            device_context: this.deviceContext(),
          });
          this.updateStickyCta();
          console.error(error);
        } finally {
          this.loadingPromise = null;
        }
      })();

      return this.loadingPromise;
    }

    validateData(data) {
      const approvedOrder = [
        "make",
        "model",
        "generation",
        "engine_name",
        "transmission_or_tcu",
      ];
      if (
        !data ||
        data.schemaVersion !== "1.2.0" ||
        data.currency !== "EUR" ||
        data.pricesIncludeVat !== true ||
        JSON.stringify(data.selectorOrder) !== JSON.stringify(approvedOrder) ||
        !data.fallbackCopy?.missingPerformance ||
        !data.fallbackCopy?.missingPrice ||
        !data.priceBands ||
        typeof data.priceBands !== "object" ||
        !Array.isArray(data.applications) ||
        data.applications.length === 0 ||
        data.applications.some((application) =>
          typeof application.selector?.model !== "string" ||
          !application.selector.model.trim() ||
          typeof application.selector.engineLabel !== "string" ||
          !application.selector.engineLabel.trim() ||
          !["present", "unconfirmed"].includes(application.selector.opfStatus) ||
          !Array.isArray(application.selector?.generations) ||
          application.selector.generations.length === 0 ||
          application.selector.generations.some((generation) =>
            typeof generation.id !== "string" || !generation.id.trim() ||
            typeof generation.label !== "string" || !generation.label.trim() ||
            typeof generation.verified !== "boolean" ||
            typeof generation.needsConfirmation !== "boolean")) ||
        "tuneOptions" in data ||
        "specialPackages" in data
      ) {
        throw new Error(
          "The OEM+ production dataset has an unsupported shape.",
        );
      }
    }

    setStatus(message, state) {
      this.dataset.loadState = state;
      if (this.statusText) {
        this.statusText.textContent = message;
      }
      if (this.status) {
        this.status.dataset.state = state;
      }
    }

    populateSelect(key, entries, labelResolver) {
      const select = this.selects[key];
      if (!select) {
        return;
      }

      const placeholder = new Option(
        select.dataset.placeholder || this.labels.choose,
        "",
      );
      select.replaceChildren(placeholder);

      for (const entry of entries) {
        const label = labelResolver ? labelResolver(entry) : entry;
        select.add(new Option(label, entry));
      }

      select.disabled = entries.length === 0;
      select.value = "";
    }

    resetAfter(key) {
      const index = SELECT_ORDER.indexOf(key);
      for (const downstreamKey of SELECT_ORDER.slice(index + 1)) {
        this.state[downstreamKey] = "";
        const select = this.selects[downstreamKey];
        if (select) {
          const placeholder = new Option(
            select.dataset.placeholder || this.labels.choose,
            "",
          );
          select.replaceChildren(placeholder);
          select.disabled = true;
        }
      }
      if (index < SELECT_ORDER.indexOf("generation")) {
        this.hideConditionalField("generation");
      }
      if (index < SELECT_ORDER.indexOf("engineName")) {
        this.hideConditionalField("engineName");
      }
      if (index < SELECT_ORDER.indexOf("tcu")) {
        this.hideConditionalField("tcu");
      }
      this.hideResults();
      this.resetResultSelection();
    }

    handleChange(key, { updateUrl = true } = {}) {
      this.state[key] = this.selects[key].value;
      this.resetAfter(key);
      this.state[key] = this.selects[key].value;
      this.clearButton.hidden = !this.state.make;

      if (this.state[key]) {
        if (key === "make") {
          const models = uniqueSorted(
            this.data.applications
              .filter(
                (application) => application.make === this.state.make,
              )
              .map((application) => this.modelValue(application)),
          );
          this.populateSelect("model", models);
        } else if (key === "model") {
          this.prepareGeneration();
        } else if (key === "generation") {
          this.prepareEngine();
        } else if (key === "engineName") {
          this.prepareTcu();
          this.renderResults();
        } else if (key === "tcu") {
          this.renderResults();
        }
      }

      if (updateUrl && !this.restoring) {
        this.updateUrl();
      }
      this.updateProgress();
      if (this.state[key] && !this.restoring) {
        this.revealNextControl(key);
      }
    }

    applicationsAtStage(stage) {
      return this.data.applications.filter((application) => {
        if (this.state.make && application.make !== this.state.make) {
          return false;
        }
        if (this.state.model && this.modelValue(application) !== this.state.model) {
          return false;
        }
        if (
          SELECT_ORDER.indexOf(stage) >=
            SELECT_ORDER.indexOf("generation") &&
          this.fields.generation &&
          !this.fields.generation.hidden &&
          this.state.generation &&
          !application.selector.generations.some((generation) => generation.id === this.state.generation)
        ) {
          return false;
        }
        if (
          SELECT_ORDER.indexOf(stage) >=
            SELECT_ORDER.indexOf("engineName") &&
          this.state.engineName &&
          this.engineValue(application) !== this.state.engineName
        ) {
          return false;
        }
        if (
          SELECT_ORDER.indexOf(stage) >= SELECT_ORDER.indexOf("tcu") &&
          this.state.tcu &&
          !(application.supportedTcuTypes ?? []).includes(this.state.tcu)
        ) {
          return false;
        }
        return true;
      });
    }

    modelValue(application) {
      return application.selector?.model || application.model;
    }

    generationLabel(application = null) {
      const candidates = application ? [application] : this.data?.applications || [];
      return candidates.flatMap((candidate) => candidate.selector?.generations || [])
        .find((generation) => generation.id === this.state.generation)?.label || "";
    }

    generationNeedsReview(application) {
      const generation = application.selector?.generations.find((item) => item.id === this.state.generation);
      return !generation || generation.needsConfirmation;
    }

    engineValue(application) {
      return JSON.stringify([application.engineName || "", application.factory?.ps ?? null, application.engineCode || ""]);
    }

    engineLabel(application) {
      const needsConfirmation = this.generationNeedsReview(application);
      return [application.selector?.engineLabel || application.engineName || this.labels.unspecifiedEngine,
        !needsConfirmation && !(application.reviewFlags || []).includes("performance_outputs_required") && application.factory?.ps
          ? `${application.factory.ps} PS (RacingLine)` : null,
        needsConfirmation ? this.labels.manualReview : null,
        application.engineCode].filter(Boolean).join(" · ");
    }

    selectedEngineLabel() {
      if (!this.state.engineName) return "";
      return this.selects.engineName?.selectedOptions[0]?.textContent || "";
    }

    prepareGeneration() {
      const candidates = this.applicationsAtStage("model");
      const generations = new Map(candidates.flatMap((application) =>
        application.selector.generations.map((generation) => [generation.id, generation.label]),
      ));
      this.showConditionalField("generation");
      this.populateSelect("generation", [...generations.keys()], (id) => generations.get(id));
    }

    prepareEngine() {
      const candidates = this.applicationsAtStage("engineName");
      const engines = new Map(candidates.map((application) => [this.engineValue(application), this.engineLabel(application)]));
      this.showConditionalField("engineName");
      this.populateSelect("engineName", uniqueSorted([...engines.keys()]), (value) => engines.get(value));
    }

    prepareTcu() {
      const candidates = this.applicationsAtStage("engineName");
      const tcuTypes = uniqueSorted(
        candidates.flatMap(
          (application) => application.supportedTcuTypes ?? [],
        ),
      );
      if (tcuTypes.length > 0) {
        this.showConditionalField("tcu");
        this.populateSelect("tcu", tcuTypes);
        this.selects.tcu.options[0].textContent = this.labels.uncertainTcu;
      } else {
        this.hideConditionalField("tcu");
      }
    }

    showConditionalField(key) {
      if (this.fields[key]) {
        this.fields[key].hidden = false;
      }
    }

    hideConditionalField(key) {
      if (this.fields[key]) {
        this.fields[key].hidden = true;
      }
      if (this.selects[key]) {
        this.selects[key].disabled = true;
        this.selects[key].value = "";
      }
      this.state[key] = "";
    }

    valueOrEmpty(value) {
      return value === undefined || value === null || value === ""
        ? EMPTY_VALUE
        : String(value);
    }

    renderResults() {
      const applications = this.applicationsAtStage("tcu");
      this.resultsGrid.replaceChildren();
      this.empty.hidden = applications.length > 0;
      this.results.hidden = applications.length === 0;
      this.refreshPresentation();

      if (applications.length === 0) {
        this.emptyTitle.textContent = this.labels.noMatch;
        this.emptyText.textContent = this.labels.noMatchText;
        this.setStatus(this.labels.noMatch, "ready");
        const noMatchKey = [
          this.state.make,
          this.state.model,
          this.state.generation,
          this.state.engineName,
          this.state.tcu,
          "no-match",
        ].join("|");
        if (noMatchKey !== this.lastTrackedResult) {
          this.lastTrackedResult = noMatchKey;
          this.publish("boosthaus_oem_no_exact_match", {
            event_source: "oem_configurator",
            device_context: this.deviceContext(),
          });
        }
        this.updateProgress();
        this.updateViberLinks();
        this.updateStickyCta();
        return;
      }

      const resultLabel =
        applications.length === 1
          ? this.labels.resultSingular
          : this.labels.resultPlural;
      this.resultsTitle.textContent =
        applications.length === 1
          ? this.labels.resultsFoundSingle
          : this.labels.resultsFoundPlural;
      applications.forEach((application, index) => {
        this.resultsGrid.append(
          this.buildApplicationCard(
            application,
            index + 1,
            applications.length,
          ),
        );
      });
      if (applications.length === 1) {
        const application = applications[0];
        const option = application.options.find((item) => item.category === "stage") || application.options[0];
        const choices = this.packageChoices(application);
        const choice = choices.length === 1 ? choices[0] : null;
        const optionControl = [...this.querySelectorAll("[data-option-key]")].find((control) => control.dataset.optionKey === option?.key);
        const packageControl = [...this.querySelectorAll("[data-package-type]")].find((control) => control.dataset.packageType === choice?.type);
        if (optionControl) this.selectOption(application, option, optionControl, { updateUrl: false });
        if (choice && packageControl) this.selectPackage(application, choice, packageControl, { updateUrl: false });
      }
      this.setStatus(`${applications.length} ${resultLabel}`, "ready");
      this.updateProgress();

      const vehicleKey = [
        this.state.make,
        this.state.model,
        this.state.generation,
        this.state.engineName,
      ].join("|");
      if (vehicleKey !== this.lastTrackedVehicle) {
        this.lastTrackedVehicle = vehicleKey;
        this.publish("boosthaus_oem_vehicle_selected", {
          event_source: "oem_configurator",
          result_count: applications.length,
          application_id:
            applications.length === 1 ? applications[0].id : undefined,
          device_context: this.deviceContext(),
        });
      }
      if (vehicleKey !== this.lastTrackedResult) {
        this.lastTrackedResult = vehicleKey;
        this.publish("boosthaus_oem_matching_result_displayed", {
          event_source: "oem_configurator",
          result_count: applications.length,
          application_id:
            applications.length === 1 ? applications[0].id : undefined,
          device_context: this.deviceContext(),
        });
      }
      this.updateViberLinks();
      this.updateStickyCta();
    }

    buildApplicationCard(application, resultIndex, resultCount) {
      const card = createElement("article", "bh-oem-result-card");
      card.dataset.applicationId = application.id;
      if (resultCount > 1) {
        const header = createElement("header", "bh-oem-result-card__header");
        header.append(
          createElement("p", "bh-oem-kicker", `${this.labels.variant} ${resultIndex}`),
          createElement("h3", "bh-oem-result-card__title", [
            this.engineLabel(application),
            (application.supportedTcuTypes ?? []).join(" / "),
          ].filter(Boolean).join(" · ")),
        );
        card.append(header);
      }
      const performance = createElement("div", "bh-oem-result-main");
      performance.append(createElement("h3", "bh-oem-choice-heading", this.labels.stageHeading));
      const stages = createElement("div", "bh-oem-stage-control");
      stages.setAttribute("role", "radiogroup");
      stages.setAttribute("aria-label", this.labels.stageHeading);
      const stageOptions = application.options.filter((option) => option.category !== "feature");
      for (const option of stageOptions) stages.append(this.buildOptionButton(application, option));
      if (!stageOptions.length) {
        stages.append(createElement("p", "bh-oem-options__empty", this.labels.noOptions));
        const manual = createElement("button", "bh-oem-button bh-oem-button--secondary", this.labels.inquireApplication);
        manual.type = "button";
        manual.addEventListener("click", () => this.selectManualInquiry(application));
        performance.append(manual);
      }
      performance.append(stages);
      const detail = createElement("div", "bh-oem-stage-detail");
      detail.dataset.bhOemStageDetail = application.id;
      detail.setAttribute("aria-live", "polite");
      detail.setAttribute("aria-atomic", "true");
      detail.append(createElement("p", "bh-oem-performance-fallback", this.labels.selectOption));
      performance.append(detail);
      card.append(performance, this.buildQuoteSummary(application));

      const technical = createElement("details", "bh-oem-technical");
      technical.append(createElement("summary", null, this.labels.technicalDetails));
      const meta = createElement("dl", "bh-oem-meta");
      this.appendDefinition(meta, this.labels.fuel, this.translateSourceValue(application.fuelType) || this.labels.unknown);
      this.appendDefinition(meta, this.labels.engineCode, application.engineCode || this.labels.unknown);
      this.appendDefinition(meta, this.labels.tcu, (application.supportedTcuTypes ?? []).join(" / ") || this.labels.unknown);
      technical.append(meta);
      for (const note of [
        application.unlockRequirement, application.tcuRecommendation,
        application.requiredHardware, application.recommendedHardware,
        application.applicationNotes,
      ].filter(Boolean)) technical.append(createElement("p", null, this.translateSourceValue(note)));
      technical.append(createElement("p", "bh-oem-disclaimer", this.labels.hardwareNotIncluded));
      const features = application.options.filter((option) => option.category === "feature");
      if (features.length) {
        technical.append(createElement("h4", "bh-oem-features__title", this.labels.extraOptions));
        const featureGroup = createElement("div", "bh-oem-features");
        featureGroup.setAttribute("role", "radiogroup");
        featureGroup.setAttribute("aria-label", this.labels.extraOptions);
        for (const option of features) featureGroup.append(this.buildOptionButton(application, option));
        technical.append(featureGroup);
      }
      const footer = createElement("div", "bh-oem-result-card__footer");
      const manual = createElement("button", "bh-oem-text-button", this.labels.inquireApplication);
      manual.type = "button";
      manual.addEventListener("click", () => this.selectManualInquiry(application));
      footer.append(manual);
      if (this.showSource && application.ecuSourceUrl) {
        const link = createElement("a", "bh-oem-source-link", this.labels.source);
        link.href = application.ecuSourceUrl;
        link.target = "_blank";
        link.rel = "nofollow noopener";
        footer.append(link);
      }
      technical.append(footer);
      card.append(technical);
      return card;
    }

    icon(name) {
      const icon = createElement("span", `bh-oem-icon bh-oem-icon--${name}`);
      icon.setAttribute("aria-hidden", "true");
      return icon;
    }

    isAdvancedStage(option) {
      return /^stage_[2-9]/.test(option.key);
    }

    bindRadioKeyboard(button) {
      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
        const controls = [...button.parentElement.querySelectorAll('[role="radio"]')];
        const index = controls.indexOf(button);
        const next = event.key === "Home" ? 0 : event.key === "End" ? controls.length - 1 :
          (index + (["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1) + controls.length) % controls.length;
        event.preventDefault();
        controls[next].focus();
        controls[next].click();
      });
    }

    buildOptionButton(application, option) {
      const button = createElement("button", "bh-oem-option");
      button.type = "button";
      button.setAttribute("role", "radio");
      button.setAttribute("aria-checked", "false");
      button.dataset.applicationId = application.id;
      button.dataset.optionKey = option.key;
      button.append(createElement("span", "bh-oem-option__title", option.label));
      if (this.isAdvancedStage(option) || this.generationNeedsReview(application)) {
        button.append(createElement("span", "bh-oem-option__review", ` · ${this.labels.stageReview}`));
      }
      button.addEventListener("click", () => this.selectOption(application, option, button));
      this.bindRadioKeyboard(button);
      return button;
    }

    buildPerformancePanel(application, option) {
      const panel = createElement("div", "bh-oem-performance-panel");
      panel.append(createElement("p", "bh-oem-performance-panel__eyebrow", [option.label, option.fuelRon?.length ? `RON ${option.fuelRon.join(" / ")}` : null].filter(Boolean).join(" · ")));
      const tcuOnly = this.selected.packageType === "tcu" && this.selected.applicationId === application.id;
      if (this.showOutputs && application.factory && option.software && !this.generationNeedsReview(application) && !tcuOnly) {
        const metrics = createElement("div", "bh-oem-metrics");
        for (const [key, unit, label] of [["ps", "PS", this.labels.power], ["nm", "Nm", this.labels.torque]]) {
          const metric = createElement("div", "bh-oem-metric");
          const value = createElement("p", "bh-oem-metric__value");
          value.dataset.digits = String(option.software[key]).length;
          value.append(createElement("strong", null, option.software[key]), createElement("span", null, unit));
          metric.append(value, createElement("p", "bh-oem-metric__label", label));
          metrics.append(metric);
        }
        panel.append(metrics);
        const comparisons = createElement("div", "bh-oem-comparisons");
        for (const [key, unit, label] of [["ps", "PS", this.labels.power], ["nm", "Nm", this.labels.torque]]) {
          const comparison = createElement("div", "bh-oem-comparison");
          if (key === "nm") comparison.append(createElement("h4", "bh-oem-comparison__heading", this.labels.torque));
          const max = Math.max(application.factory[key], option.software[key]) * 1.18;
          for (const [name, value, tuned] of [[this.labels.before, application.factory[key], false], [option.label, option.software[key], true]]) {
            const row = createElement("div", `bh-oem-bar-row${tuned ? " bh-oem-bar-row--tuned" : ""}`);
            row.append(createElement("span", "bh-oem-bar-row__label", name));
            const bar = createElement("meter", "bh-oem-bar");
            bar.min = 0;
            bar.max = max;
            bar.value = value;
            bar.setAttribute("aria-label", `${label} · ${name}`);
            bar.setAttribute("aria-valuetext", `${value} ${unit}`);
            bar.textContent = `${value} ${unit}`;
            row.append(bar, createElement("strong", "bh-oem-bar-row__value", `${value} ${unit}`));
            comparison.append(row);
          }
          comparisons.append(comparison);
        }
        panel.append(comparisons);
        panel.append(createElement("p", "bh-oem-gain", `${this.labels.gain}: ${this.formatPerformanceNumber(option.software.ps - application.factory.ps, true)} PS · ${this.formatPerformanceNumber(option.software.nm - application.factory.nm, true)} Nm`));
      } else if (this.showOutputs || tcuOnly) {
        panel.append(createElement("p", "bh-oem-performance-fallback", tcuOnly ? this.labels.tcuOnlyNote : option.category === "feature" ? this.labels.featureNote : this.data.fallbackCopy.missingPerformance));
      }
      const note = createElement("div", "bh-oem-stage-note");
      note.append(this.icon("info-circle"), createElement("p", null, this.isAdvancedStage(option) ? this.labels.opfCheck : this.labels.tcuCheck));
      panel.append(note);
      for (const [label, value] of [
        [this.labels.requiredHardware, option.requiredHardware],
        [this.labels.recommendedHardware, option.recommendedHardware],
        ["", option.notes],
      ]) {
        if (value) panel.append(createElement("p", "bh-oem-stage-fact", [label, this.translateSourceValue(value)].filter(Boolean).join(": ")));
      }
      return panel;
    }

    buildQuoteSummary(application) {
      const quote = createElement("aside", "bh-oem-quote");
      quote.setAttribute("aria-label", this.labels.configurationTitle);
      quote.append(createElement("h3", "bh-oem-quote__title", this.labels.configurationTitle));
      const packages = createElement("details", "bh-oem-package-picker");
      const summary = createElement("summary");
      summary.append(
        createElement("span", "bh-oem-kicker", this.labels.packageHeading),
        createElement("span", "bh-oem-package-picker__value", this.labels.selectPackage),
        this.icon("chevron-right"),
      );
      packages.append(summary, this.buildPackageChoices(application));
      quote.append(packages);
      const price = createElement("div", "bh-oem-quote__price");
      price.append(createElement("p", "bh-oem-kicker", this.labels.preliminaryPrice), createElement("p", "bh-oem-quote__amount", "—"));
      if (!this.showPrices) price.hidden = true;
      quote.append(price);
      const featurePriceNote = createElement("p", "bh-oem-quote__feature-note", this.labels.featurePriceNote);
      featurePriceNote.hidden = true;
      quote.append(featurePriceNote);
      const includes = createElement("ul", "bh-oem-quote__includes");
      quote.append(createElement("p", "bh-oem-kicker", this.labels.includes), includes);
      const submit = createElement("button", "bh-oem-button bh-oem-button--primary", this.labels.submitSelection);
      submit.type = "button";
      submit.disabled = true;
      submit.dataset.bhOemCardSubmit = application.id;
      submit.append(this.icon("chevron-right"));
      submit.addEventListener("click", () => this.commitLeadSelection(application));
      quote.append(submit);
      if (this.dataset.viberLink) {
        const viber = createElement("a", "bh-oem-button bh-oem-button--secondary", this.labels.viberDiscuss);
        viber.href = this.dataset.viberLink;
        viber.dataset.bhOemViber = "";
        viber.append(this.icon("message-circle"));
        viber.addEventListener("click", () => this.publish("boosthaus_oem_viber_click", { event_source: "oem_configurator", cta_location: "result", ...this.analyticsSelection() }));
        quote.append(viber);
      }
      const note = createElement("p", "bh-oem-quote__note");
      note.append(this.icon("info-circle"), createElement("span", null, this.labels.checkNote));
      quote.append(note);
      return quote;
    }

    refreshPresentation() {
      const applications = this.applicationsAtStage("tcu");
      const ready = Boolean(this.state.engineName && applications.length);
      this.dataset.view = ready ? "results" : "selection";
      if (this.contextWrap) this.contextWrap.hidden = !ready;
      if (this.contextText && ready) this.contextText.textContent = [
        this.state.make, this.state.model, this.generationLabel(), this.selectedEngineLabel(),
      ].filter(Boolean).join(" / ");
      if (this.heroTitle) this.heroTitle.textContent = ready
        ? this.labels.personalTitle.replace("{model}", this.state.model)
        : this.initialTitle;
      if (this.heroIntro && ready) {
        this.heroIntro.textContent = this.labels.personalSubtitle;
      } else if (this.heroIntro) {
        this.heroIntro.replaceChildren(...this.initialIntro.map((node) => node.cloneNode(true)));
      }
      if (this.doneEdit) this.doneEdit.hidden = !ready;
      if (this.results) this.results.dataset.multiple = String(applications.length > 1);
    }

    refreshResultPanels() {
      const applications = this.data?.applications ?? [];
      this.querySelectorAll(".bh-oem-result-card").forEach((card) => {
        const application = applications.find((item) => item.id === card.dataset.applicationId);
        if (!application) return;
        const active = this.selected.applicationId === application.id;
        const option = active ? application.options.find((item) => item.key === this.selected.optionKey) : null;
        const detail = card.querySelector("[data-bh-oem-stage-detail]");
        if (detail) detail.replaceChildren(option ? this.buildPerformancePanel(application, option) : createElement("p", "bh-oem-performance-fallback", this.labels.selectOption));
        const choice = active ? this.packageChoices(application).find((item) => item.type === this.selected.packageType) : null;
        const featurePriceNote = card.querySelector(".bh-oem-quote__feature-note");
        if (featurePriceNote) featurePriceNote.hidden = option?.category !== "feature";
        const label = card.querySelector(".bh-oem-package-picker__value");
        if (label) label.textContent = choice?.label || this.labels.selectPackage;
        const amount = card.querySelector(".bh-oem-quote__amount");
        if (amount) {
          amount.textContent = choice ? choice.band ? this.formatMoney(choice.band.priceEurVatIncl) : this.data.fallbackCopy.missingPrice : "—";
          amount.dataset.pending = String(Boolean(choice && !choice.band));
        }
        const includes = card.querySelector(".bh-oem-quote__includes");
        if (includes) {
          includes.replaceChildren();
          const rows = [];
          if (!choice) rows.push(["info-circle", this.labels.offerPending]);
          if (choice?.type === "ecu" || choice?.type === "ecuTcuPackage") rows.push(["cpu", this.labels.ecuIncluded]);
          if (choice?.type === "tcu" || choice?.type === "ecuTcuPackage") rows.push(["settings", this.labels.tcuIncluded]);
          if (choice) rows.push(["tool", choice.band?.installationIncluded === true ? this.labels.installationIncluded : this.labels.installationConfirm]);
          for (const [icon, text] of rows) {
            const row = createElement("li");
            row.append(this.icon(icon), createElement("span", null, text));
            includes.append(row);
          }
        }
      });
    }

    buildPerformanceComparison(factory, software) {
      const comparison = createElement(
        "span",
        "bh-oem-performance-comparison",
      );
      comparison.append(
        this.buildPerformanceValue(this.labels.stock, factory.ps, factory.nm),
        this.buildPerformanceValue(
          this.labels.tuned,
          software.ps,
          software.nm,
        ),
        this.buildPerformanceValue(
          this.labels.gain,
          software.ps - factory.ps,
          software.nm - factory.nm,
          true,
        ),
      );
      return comparison;
    }

    buildPerformanceValue(label, ps, nm, signed = false) {
      const item = createElement("span", "bh-oem-performance-value");
      item.append(
        createElement("span", "bh-oem-performance-value__label", label),
        createElement(
          "strong",
          "bh-oem-performance-value__number",
          `${this.formatPerformanceNumber(ps, signed)} PS`,
        ),
        createElement(
          "span",
          "bh-oem-performance-value__torque",
          `${this.formatPerformanceNumber(nm, signed)} Nm`,
        ),
      );
      return item;
    }

    formatPerformanceNumber(value, signed) {
      if (!signed || value <= 0) {
        return String(value);
      }
      return `+${value}`;
    }

    buildPackageChoices(application) {
      const section = createElement("div", "bh-oem-packages");
      section.append(
        createElement(
          "p",
          "bh-oem-choice-heading",
          this.labels.priceSelection,
        ),
      );
      const group = createElement("div", "bh-oem-prices");
      group.setAttribute("role", "radiogroup");
      group.setAttribute("aria-label", this.labels.priceSelection);

      const choices = this.packageChoices(application);
      for (const choice of choices) {
        const button = createElement("button", "bh-oem-price");
        button.type = "button";
        button.setAttribute("role", "radio");
        button.setAttribute("aria-checked", "false");
        button.dataset.applicationId = application.id;
        button.dataset.packageType = choice.type;
        button.append(
          createElement("span", "bh-oem-price__label", choice.label),
          createElement(
            "span",
            "bh-oem-price__includes",
            choice.includes,
          ),
          createElement(
            "span",
            "bh-oem-price__context",
            choice.context,
          ),
        );

        if (choice.band && this.showPrices) {
          button.append(
            createElement(
              "strong",
              "bh-oem-price__value",
              this.formatMoney(choice.band.priceEurVatIncl),
            ),
          );
          const installationCopy =
            choice.band.installationIncluded === true
              ? this.labels.installationIncluded
              : this.labels.installationConfirm;
          button.append(
            createElement(
              "span",
              "bh-oem-price__meta",
              `${this.labels.vatIncluded} • ${installationCopy}`,
            ),
          );
        } else if (!choice.band) {
          button.append(
            createElement(
              "strong",
              "bh-oem-price__value bh-oem-price__value--fallback",
              this.data.fallbackCopy.missingPrice,
            ),
          );
        }

        button.append(
          createElement(
            "span",
            "bh-oem-price__action",
            this.labels.selectPackage,
          ),
        );
        button.addEventListener("click", () =>
          this.selectPackage(application, choice, button),
        );
        this.bindRadioKeyboard(button);
        group.append(button);
      }
      section.append(group);
      section.append(
        createElement(
          "p",
          "bh-oem-prices__note",
          this.labels.hardwareNotIncluded,
        ),
      );
      return section;
    }

    packageChoices(application) {
      const choices = [];
      for (const [type, labelKey] of PACKAGE_TYPES) {
        const priceKey = application.priceKeys?.[type];
        if (!priceKey) {
          continue;
        }
        const band = this.data.priceBands[priceKey] ?? null;
        choices.push({
          type,
          label: this.labels[labelKey],
          includes:
            type === "ecu"
              ? this.labels.packageEcuIncludes
              : type === "tcu"
                ? this.labels.packageTcuIncludes
                : this.labels.packageCombinedIncludes,
          context:
            type === "ecu"
              ? this.labels.packageEcuContext
              : type === "tcu"
                ? this.labels.packageTcuContext
                : this.labels.packageCombinedContext,
          priceKey,
          band:
            band &&
            typeof band.priceEurVatIncl === "number" &&
            band.priceEurVatIncl > 0
              ? band
              : null,
        });
      }

      if (choices.length === 0) {
        choices.push({
          type: "confirmation",
          label: this.labels.packageConfirmation,
          includes: this.labels.packageConfirmationIncludes,
          context: this.labels.packageConfirmationContext,
          priceKey: "",
          band: null,
        });
      }
      return choices;
    }

    selectOption(application, option, control, { updateUrl = true } = {}) {
      this.ensureApplicationContext(application);
      this.querySelectorAll(".bh-oem-option").forEach((button) => {
        button.setAttribute("aria-checked", "false");
        button.tabIndex = -1;
        const action = button.querySelector(".bh-oem-option__action");
        if (action) {
          action.textContent = this.labels.selectOption;
        }
      });
      control.setAttribute("aria-checked", "true");
      control.tabIndex = 0;
      const selectedAction = control.querySelector(".bh-oem-option__action");
      if (selectedAction) {
        selectedAction.textContent = this.labels.selected;
      }
      this.selected.optionKey = option.key;
      this.updateLeadSelection();
      if (updateUrl && !this.restoring) {
        this.updateUrl();
        this.publish("boosthaus_oem_option_selected", {
          event_source: "oem_configurator",
          application_id: application.id,
          option_key: option.key,
          device_context: this.deviceContext(),
        });
      }
      this.updateProgress();
    }

    selectPackage(
      application,
      choice,
      control,
      { updateUrl = true } = {},
    ) {
      this.ensureApplicationContext(application);
      this.querySelectorAll(".bh-oem-price").forEach((button) => {
        button.setAttribute("aria-checked", "false");
        button.tabIndex = -1;
        const action = button.querySelector(".bh-oem-price__action");
        if (action) {
          action.textContent = this.labels.selectPackage;
        }
      });
      control.setAttribute("aria-checked", "true");
      control.tabIndex = 0;
      const picker = control.closest(".bh-oem-package-picker");
      if (picker) {
        picker.open = false;
        if (updateUrl) picker.querySelector("summary")?.focus({ preventScroll: true });
      }
      const selectedAction = control.querySelector(".bh-oem-price__action");
      if (selectedAction) {
        selectedAction.textContent = this.labels.selected;
      }
      this.selected.packageType = choice.type;
      this.selected.priceKey = choice.priceKey;
      this.updateLeadSelection();
      if (updateUrl && !this.restoring) {
        this.updateUrl();
        this.publish("boosthaus_oem_package_selected", {
          event_source: "oem_configurator",
          application_id: application.id,
          package_type: choice.type,
          price_available: Boolean(choice.band),
          price_eur: choice.band?.priceEurVatIncl,
          device_context: this.deviceContext(),
        });
      }
      this.updateProgress();
      this.updateViberLinks();
      this.updateStickyCta();
      this.refreshResultPanels();
    }

    ensureApplicationContext(application) {
      if (this.selected.applicationId === application.id) {
        return;
      }
      this.selected = {
        applicationId: application.id,
        optionKey: "",
        packageType: "",
        priceKey: "",
      };
      this.querySelectorAll(".bh-oem-option").forEach((button) => {
        button.setAttribute("aria-checked", "false");
        const action = button.querySelector(".bh-oem-option__action");
        if (action) {
          action.textContent = this.labels.selectOption;
        }
      });
      this.querySelectorAll(".bh-oem-price").forEach((button) => {
        button.setAttribute("aria-checked", "false");
        const action = button.querySelector(".bh-oem-price__action");
        if (action) {
          action.textContent = this.labels.selectPackage;
        }
      });
    }

    updateLeadSelection() {
      const application = this.data.applications.find(
        (candidate) => candidate.id === this.selected.applicationId,
      );
      const option = application?.options.find(
        (candidate) => candidate.key === this.selected.optionKey,
      );
      const packageChoice = application
        ? this.packageChoices(application).find(
            (candidate) =>
              candidate.type === this.selected.packageType &&
              candidate.priceKey === this.selected.priceKey,
          )
        : null;

      if (!application) {
        this.resetLeadSelection();
        return;
      }

      const selectedTcu =
        this.state.tcu ||
        (application.supportedTcuTypes?.length === 1
          ? application.supportedTcuTypes[0]
          : this.labels.uncertainTcu);
      const vehicleSummary = [
        application.make,
        this.modelValue(application),
        this.generationLabel(application),
        this.engineLabel(application),
      ]
        .filter(Boolean)
        .join(" • ");
      const priceSummary = packageChoice
        ? this.showPrices
          ? packageChoice.band
            ? `${this.formatMoney(packageChoice.band.priceEurVatIncl)} • ${
                this.labels.vatIncluded
              }`
            : this.data.fallbackCopy.missingPrice
          : this.data.fallbackCopy.missingPrice
        : "—";
      const installationSummary = packageChoice
        ? packageChoice.band
          ? packageChoice.band.installationIncluded === true
            ? this.labels.installationIncluded
            : this.labels.installationConfirm
          : this.labels.installationConfirm
        : "—";
      const summaryValues = {
        vehicle: vehicleSummary,
        transmission: selectedTcu,
        stage: option?.label || "—",
        package: packageChoice?.label || "—",
        price: priceSummary,
        installation: installationSummary,
      };
      for (const [key, value] of Object.entries(summaryValues)) {
        if (this.summaryFields[key]) {
          this.summaryFields[key].textContent = value;
        }
      }
      this.summary.hidden = true;
      if (this.summaryDetails) {
        this.summaryDetails.hidden = false;
      }
      if (this.editButton) {
        this.editButton.hidden = false;
      }

      const values = {
        dataVersion: `${this.data.schemaVersion} / ${this.data.generatedDate}`,
        applicationId: application.id,
        make: application.make,
        model: this.modelValue(application),
        generation: this.generationLabel(application),
        year: application.year?.raw ?? "",
        engineName: this.engineLabel(application),
        tcu: selectedTcu,
        optionKey: option?.key ?? "",
        option: option?.label ?? "",
        optionCategory: option?.category ?? "",
        package: packageChoice?.label ?? "",
        priceKey: packageChoice?.priceKey ?? "",
        priceEur:
          packageChoice?.band?.priceEurVatIncl !== undefined
            ? `${packageChoice.band.priceEurVatIncl} EUR`
            : "",
        installationIncluded:
          packageChoice?.band?.installationIncluded === true
            ? this.labels.yes
            : "",
      };
      for (const [key, value] of Object.entries(values)) {
        this.setHiddenValue(key, value);
      }

      const selectionComplete = Boolean(option && packageChoice);
      this.querySelectorAll("[data-bh-oem-card-submit]").forEach((button) => {
        button.disabled =
          button.dataset.bhOemCardSubmit !== application.id ||
          !selectionComplete;
      });
      this.querySelectorAll("[data-bh-oem-card-offer]").forEach((offer) => {
        const value = offer.querySelector(".bh-oem-offer-summary__value");
        if (!value) {
          return;
        }
        value.textContent =
          offer.dataset.bhOemCardOffer === application.id
            ? [
                option?.label,
                packageChoice?.label,
                packageChoice?.band
                  ? this.formatMoney(packageChoice.band.priceEurVatIncl)
                  : packageChoice
                    ? this.data.fallbackCopy.missingPrice
                    : null,
              ]
                .filter(Boolean)
                .join(" • ") || this.labels.offerPending
            : this.labels.offerPending;
      });
      if (!selectionComplete) {
        this.setStatus(this.labels.incompleteSelection, "ready");
      }
      this.refreshResultPanels();
      this.querySelectorAll('[role="radiogroup"]').forEach((group) => {
        const controls = [...group.querySelectorAll('[role="radio"]')];
        const current = controls.find((button) => button.getAttribute("aria-checked") === "true") || controls[0];
        controls.forEach((button) => { button.tabIndex = button === current ? 0 : -1; });
      });
      this.updateProgress();
      this.updateViberLinks();
      this.updateStickyCta();
    }

    commitLeadSelection(application) {
      const selectionComplete =
        this.selected.applicationId === application.id &&
        this.selected.optionKey &&
        this.selected.packageType;
      if (!selectionComplete) {
        this.setStatus(this.labels.incompleteSelection, "ready");
        return;
      }
      this.publish("boosthaus_oem_lead_open", {
        event_source: "oem_configurator",
        cta_location: "result",
        ...this.analyticsSelection(),
      });
      this.publish("boosthaus_oem_primary_offer_click", {
        event_source: "oem_configurator",
        cta_location: "result",
        ...this.analyticsSelection(),
      });
      this.scrollToForm();
    }

    selectManualInquiry(application = null) {
      if (application) {
        this.selected = {
          applicationId: application.id,
          optionKey: "",
          packageType: "",
          priceKey: "",
        };
        this.updateLeadSelection();
      } else {
        this.selected = {
          applicationId: "",
          optionKey: "",
          packageType: "",
          priceKey: "",
        };
        const manualVehicle = [
          this.state.make,
          this.state.model,
          this.generationLabel(),
          this.selectedEngineLabel(),
          this.state.tcu,
        ]
          .filter(Boolean)
          .join(" • ");
        this.summary.hidden = false;
        this.summary.textContent =
          manualVehicle || this.summary.dataset.defaultText;
        if (this.summaryDetails) {
          this.summaryDetails.hidden = true;
        }
        if (this.editButton) {
          this.editButton.hidden = !manualVehicle;
        }
        const manualValues = {
          make: this.state.make,
          model: this.state.model,
          generation: this.generationLabel(),
          year: "",
          engineName: this.selectedEngineLabel(),
          tcu: this.state.tcu,
        };
        for (const [key, value] of Object.entries(manualValues)) {
          this.setHiddenValue(key, value);
        }
      }
      this.setStatus(this.labels.ready, "ready");
      this.updateUrl();
      this.publish("boosthaus_oem_manual_enquiry", {
        event_source: "oem_configurator",
        application_id: application?.id,
        device_context: this.deviceContext(),
      });
      this.updateViberLinks();
      this.updateStickyCta();
      this.scrollToForm();
    }

    scrollToForm() {
      this.formSection?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
      this.form?.querySelector('input[name="name"]')?.focus({ preventScroll: true });
    }

    editSelection() {
      this.dataset.editing = "true";
      this.contextEdit?.setAttribute("aria-expanded", "true");
      this.selectorSection?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
      window.setTimeout(() => {
        this.selects.make?.focus({ preventScroll: true });
      }, 220);
    }

    selectionContext() {
      const application = this.data?.applications.find(
        (candidate) => candidate.id === this.selected.applicationId,
      );
      const option = application?.options.find(
        (candidate) => candidate.key === this.selected.optionKey,
      );
      const packageChoice = application
        ? this.packageChoices(application).find(
            (candidate) =>
              candidate.type === this.selected.packageType &&
              candidate.priceKey === this.selected.priceKey,
          )
        : null;
      return { application, option, packageChoice };
    }

    analyticsSelection() {
      const { application, option, packageChoice } =
        this.selectionContext();
      return {
        application_id: application?.id || undefined,
        option_key: option?.key || undefined,
        stage: option?.label || undefined,
        package_type: packageChoice?.type || undefined,
        price_available: Boolean(packageChoice?.band),
        price_eur: packageChoice?.band?.priceEurVatIncl,
        device_context: this.deviceContext(),
      };
    }

    deviceContext() {
      return window.matchMedia("(max-width: 749px)").matches
        ? "mobile"
        : "desktop";
    }

    continueJourney() {
      if (!this.data) {
        this.selectorSection?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
          block: "start",
        });
        this.loadData({ restore: this.hasRestorableUrlState() });
        return;
      }

      const { application, option, packageChoice } =
        this.selectionContext();
      if (application && option && packageChoice) {
        this.commitLeadSelection(application);
        return;
      }

      if (!this.results.hidden) {
        const card =
          this.querySelector(
            `[data-application-id="${this.selected.applicationId}"]`,
          ) || this.resultsGrid.querySelector(".bh-oem-result-card");
        const target = option
          ? card?.querySelector(".bh-oem-package-picker")
          : card?.querySelector(".bh-oem-stage-control");
        if (option && target) target.open = true;
        this.scrollToElement(target || this.results);
        const control = target?.querySelector('[role="radio"][aria-checked="true"]') || target?.querySelector('[role="radio"]');
        control?.focus({ preventScroll: true });
        return;
      }

      if (!this.empty.hidden) {
        this.selectManualInquiry();
        return;
      }

      this.scrollToElement(this.selectorSection);
      this.selects.make?.focus({ preventScroll: true });
    }

    scrollToElement(element) {
      element?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    }

    handleStickyAction() {
      const { application } = this.selectionContext();
      if (this.stickyState === "offer" && application) {
        this.publish("boosthaus_oem_primary_offer_click", {
          event_source: "oem_configurator",
          cta_location: "mobile_sticky",
          ...this.analyticsSelection(),
        });
        this.scrollToForm();
        return;
      }
      if (this.stickyState === "manual") {
        this.selectManualInquiry(application);
        return;
      }
      this.continueJourney();
    }

    updateStickyCta() {
      if (!this.sticky || !this.stickyAction) {
        return;
      }
      if (
        window.innerWidth >= 750 ||
        this.formInView ||
        this.form?.dataset.formState === "success"
      ) {
        this.sticky.hidden = true;
        this.dataset.stickyVisible = "false";
        return;
      }

      const { application, option, packageChoice } =
        this.selectionContext();
      let state = "";
      let label = "";
      if (this.dataset.loadState === "error" || !this.empty.hidden) {
        state = "manual";
        label = this.labels.stickyManual;
      } else if (!this.results.hidden) {
        if (!option) {
          state = "option";
          label = this.labels.stickyOption;
        } else if (!packageChoice) {
          state = "package";
          label = this.labels.stickyPackage;
        } else {
          state = "offer";
          label = this.labels.stickyOffer;
        }
      }

      if (!state) {
        this.sticky.hidden = true;
        this.dataset.stickyVisible = "false";
        return;
      }

      const vehicleLabel = [
        application?.make || this.state.make,
        application?.modelDisplay ||
          application?.model ||
          this.state.model,
      ]
        .filter(Boolean)
        .join(" ");
      const detailLabel = [
        option?.label,
        packageChoice?.label,
        packageChoice?.band
          ? this.formatMoney(packageChoice.band.priceEurVatIncl)
          : null,
      ]
        .filter(Boolean)
        .join(" • ");
      this.stickyState = state;
      this.sticky.dataset.state = state;
      this.stickyAction.textContent = label;
      if (this.stickySummary) {
        this.stickySummary.textContent =
          vehicleLabel || this.labels.stickyManual;
      }
      if (this.stickyDetail) {
        this.stickyDetail.textContent =
          detailLabel || this.labels.resultNextText;
      }
      this.sticky.hidden = false;
      this.dataset.stickyVisible = "true";
    }

    buildViberMessage() {
      const { application, option, packageChoice } =
        this.selectionContext();
      const shorten = (value) => {
        const text = String(value || "").trim();
        return text.length > 21 ? `${text.slice(0, 20)}…` : text;
      };
      const parts = [
        [
          application?.make || this.state.make,
          application?.modelDisplay ||
            application?.model ||
            this.state.model,
        ]
          .filter(Boolean)
          .join(" "),
        [
          this.generationLabel(application),
        ]
          .filter(Boolean)
          .join("/"),
        application ? this.engineLabel(application) : this.selectedEngineLabel(),
        this.state.tcu ||
          (application?.supportedTcuTypes?.length === 1
            ? application.supportedTcuTypes[0]
            : ""),
        [option?.label, packageChoice?.label].filter(Boolean).join("/"),
        packageChoice?.band
          ? this.formatMoney(packageChoice.band.priceEurVatIncl)
          : "",
      ]
        .filter(Boolean)
        .map(shorten);

      if (parts.length === 0) {
        return "Здравейте! Искам OEM+ оферта. Моля, помогнете ми да потвърдим съвместимостта.";
      }
      return `Здравейте! OEM+ оферта: ${parts.join(
        " | ",
      )}. Потвърдете съвместимостта.`;
    }

    updateViberLinks() {
      const href = `viber://forward?text=${encodeURIComponent(
        this.buildViberMessage(),
      )}`;
      this.querySelectorAll("[data-bh-oem-viber]").forEach((link) => {
        link.href = href;
      });
    }

    setSubmitting(submitting) {
      if (!this.form || !this.formSubmit) {
        return;
      }
      this.form.dataset.submitting = submitting ? "true" : "false";
      this.form.setAttribute("aria-busy", submitting ? "true" : "false");
      this.formSubmit.disabled = submitting;
      this.formSubmit.textContent = submitting
        ? this.labels.formSubmitting
        : this.formSubmitDefaultText;
    }

    updateProgress() {
      const vehicleComplete = Boolean(
        this.state.make &&
          this.state.model &&
          this.state.generation &&
          this.state.engineName,
      );
      const softwareComplete = Boolean(
        vehicleComplete && this.selected.optionKey,
      );
      const packageComplete = Boolean(
        softwareComplete && this.selected.packageType,
      );
      const states = {
        vehicle: vehicleComplete ? "complete" : "current",
        software: !vehicleComplete
          ? "upcoming"
          : softwareComplete
            ? "complete"
            : "current",
        package: !softwareComplete
          ? "upcoming"
          : packageComplete
            ? "complete"
            : "current",
      };

      for (const [key, item] of Object.entries(this.progressItems)) {
        const state = states[key] || "upcoming";
        item.dataset.state = state;
        if (state === "current") {
          item.setAttribute("aria-current", "step");
        } else {
          item.removeAttribute("aria-current");
        }
      }
    }

    revealNextControl(key) {
      if (this.restoring) return;
      if (this.dataset.view === "results" && this.dataset.editing !== "true") {
        this.heroTitle?.focus({ preventScroll: true });
        this.scrollToElement(this.contextWrap);
        return;
      }
      const currentIndex = SELECT_ORDER.indexOf(key);
      const nextSelect = SELECT_ORDER.slice(currentIndex + 1)
        .map((nextKey) => this.selects[nextKey])
        .find(
          (select) =>
            select &&
            !select.disabled &&
            !select.closest("[hidden]"),
        );
      if (nextSelect) {
        this.revealElement(nextSelect.closest(".bh-oem-field"));
      } else if (!this.results.hidden) {
        this.revealElement(this.results);
      }
    }

    revealElement(element) {
      if (!element || window.innerWidth >= 750 || this.restoring) {
        return;
      }
      const bounds = element.getBoundingClientRect();
      if (bounds.top >= 80 && bounds.bottom <= window.innerHeight - 24) {
        return;
      }
      element.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "nearest",
      });
    }

    captureAttribution() {
      const url = new URL(window.location.href);
      const storedAttribution = {};
      try {
        const storedValue = JSON.parse(
          window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "{}",
        );
        if (storedValue && typeof storedValue === "object") {
          for (const [key] of ATTRIBUTION_FIELDS) {
            if (typeof storedValue[key] === "string") {
              storedAttribution[key] = storedValue[key];
            }
          }
        }
      } catch {}

      const attribution = Object.fromEntries(
        ATTRIBUTION_FIELDS.map(([key, parameter]) => {
          const urlValue = (url.searchParams.get(parameter) || "").trim();
          if (urlValue) {
            return [key, urlValue];
          }
          if (key === "landingPage") {
            return [key, storedAttribution[key] || url.href];
          }
          return [key, storedAttribution[key] || ""];
        }),
      );

      try {
        window.sessionStorage.setItem(
          ATTRIBUTION_STORAGE_KEY,
          JSON.stringify(attribution),
        );
      } catch {}

      for (const [key, value] of Object.entries(attribution)) {
        this.setHiddenValue(key, value);
      }
    }

    showEmailError() {
      if (!this.emailInput || !this.emailError) {
        return;
      }
      this.emailInput.setAttribute("aria-invalid", "true");
      this.emailError.hidden = false;
      const describedBy = new Set(
        (this.emailInput.getAttribute("aria-describedby") || "")
          .split(/\s+/)
          .filter(Boolean),
      );
      if (this.emailError.id) {
        describedBy.add(this.emailError.id);
      }
      this.emailInput.setAttribute(
        "aria-describedby",
        [...describedBy].join(" "),
      );
    }

    hideEmailError() {
      if (!this.emailInput || !this.emailError) {
        return;
      }
      this.emailInput.removeAttribute("aria-invalid");
      this.emailError.hidden = true;
      const describedBy = (
        this.emailInput.getAttribute("aria-describedby") || ""
      )
        .split(/\s+/)
        .filter((id) => id && id !== this.emailError.id);
      if (describedBy.length > 0) {
        this.emailInput.setAttribute(
          "aria-describedby",
          describedBy.join(" "),
        );
      } else {
        this.emailInput.removeAttribute("aria-describedby");
      }
    }

    publish(name, payload) {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(name, payload);
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: name, ...payload });
      }
    }

    async handleFormSubmit(event) {
      event.preventDefault();
      if (
        !this.form ||
        this.form.dataset.submitting === "true" ||
        confirmedSubmissionHandled
      ) {
        return;
      }

      this.hideSubmissionError();
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        if (this.emailInput && !this.emailInput.validity.valid) {
          this.showEmailError();
        }
        return;
      }

      if (this.formSubject) {
        const details = [
          this.hiddenFields.make?.value,
          this.hiddenFields.model?.value,
          this.hiddenFields.option?.value,
        ].filter(Boolean);
        this.formSubject.value = [this.formSubjectDefault, ...details].join(
          " — ",
        );
      }

      this.setSubmitting(true);
      try {
        const response = await fetch(this.form.action, {
          method: "POST",
          body: new FormData(this.form),
          headers: {
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error(`OEM+ enquiry request failed with ${response.status}`);
        }

        confirmedSubmissionHandled = true;
        this.publish("boosthaus_oem_enquiry_success", {
          event_source: "oem_configurator",
          ...this.analyticsSelection(),
        });
        window.location.assign(SUCCESS_REDIRECT_PATH);
      } catch (error) {
        this.setSubmitting(false);
        this.showSubmissionError();
      }
    }

    showSubmissionError() {
      if (!this.formError) {
        return;
      }
      this.formError.hidden = false;
      this.formError.focus({ preventScroll: true });
    }

    hideSubmissionError() {
      if (this.formError) {
        this.formError.hidden = true;
      }
    }

    resetResultSelection() {
      this.selected = {
        applicationId: "",
        optionKey: "",
        packageType: "",
        priceKey: "",
      };
      this.resetLeadSelection();
    }

    resetLeadSelection() {
      this.summary.hidden = false;
      this.summary.textContent = this.summary.dataset.defaultText;
      if (this.summaryDetails) {
        this.summaryDetails.hidden = true;
      }
      if (this.editButton) {
        this.editButton.hidden = true;
      }
      for (const [key, input] of Object.entries(this.hiddenFields)) {
        if (key === "dataVersion" && this.data) {
          input.value = `${this.data.schemaVersion} / ${this.data.generatedDate}`;
        } else if (
          ![
            "utmSource",
            "utmMedium",
            "utmCampaign",
            "utmContent",
            "utmTerm",
            "placement",
            "fbclid",
            "landingPage",
          ].includes(key)
        ) {
          input.value = "";
        }
      }
      this.querySelectorAll("[data-bh-oem-card-submit]").forEach((button) => {
        button.disabled = true;
      });
      this.querySelectorAll(".bh-oem-offer-summary__value").forEach((value) => {
        value.textContent = this.labels.offerPending;
      });
      this.updateProgress();
      this.updateViberLinks();
      this.updateStickyCta();
    }

    setHiddenValue(key, value) {
      if (this.hiddenFields[key]) {
        this.hiddenFields[key].value = value;
      }
    }

    appendDefinition(list, term, value) {
      const group = createElement("div", "bh-oem-meta__group");
      group.append(
        createElement("dt", "bh-oem-meta__term", term),
        createElement("dd", "bh-oem-meta__value", value),
      );
      list.append(group);
    }

    translateSourceValue(value) {
      if (typeof value !== "string") {
        return value;
      }
      const normalized = value.trim().toLowerCase();
      if (normalized === "petrol") {
        return this.labels.petrol;
      }
      if (normalized === "diesel") {
        return this.labels.diesel;
      }
      if (normalized === "yes") {
        return this.labels.yes;
      }
      if (normalized === "no") {
        return this.labels.no;
      }
      return value;
    }

    formatMoney(amount) {
      return new Intl.NumberFormat("bg-BG", {
        style: "currency",
        useGrouping: "always",
        currency: this.data.currency,
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }

    needsManualReview(application) {
      const generation = application.selector?.generations.find((item) => item.id === this.state.generation);
      return (
        this.generationNeedsReview(application) ||
        !generation?.verified ||
        !application.engineName ||
        (application.reviewFlags ?? []).length > 0
      );
    }

    hideResults() {
      this.results.hidden = true;
      this.empty.hidden = true;
      this.resultsGrid.replaceChildren();
      this.refreshPresentation();
      this.updateStickyCta();
    }

    clearSelection() {
      this.state = Object.fromEntries(SELECT_ORDER.map((key) => [key, ""]));
      for (const [key, select] of Object.entries(this.selects)) {
        if (key === "make") {
          select.value = "";
          select.disabled = !this.data;
        } else {
          const placeholder = new Option(
            select.dataset.placeholder || this.labels.choose,
            "",
          );
          select.replaceChildren(placeholder);
          select.disabled = true;
        }
      }
      this.hideConditionalField("generation");
      this.hideConditionalField("engineName");
      this.hideConditionalField("tcu");
      this.hideResults();
      this.resetResultSelection();
      this.clearButton.hidden = true;
      this.setStatus(this.labels.ready, "ready");
      this.updateUrl();
      this.selects.make.focus();
    }

    hasRestorableUrlState() {
      const url = new URL(window.location.href);
      return Object.values(URL_KEYS).some((key) => {
        const value = url.searchParams.get(key);
        return value !== null && value.trim() !== "";
      });
    }

    restoreFromUrl() {
      const url = new URL(window.location.href);
      const requested = Object.fromEntries(
        Object.entries(URL_KEYS).map(([stateKey, parameter]) => [
          stateKey,
          url.searchParams.get(parameter) ?? "",
        ]),
      );
      if (requested.model) {
        const sourceModel = this.data.applications.find((application) =>
          application.make === requested.make && application.model === requested.model);
        if (sourceModel) requested.model = this.modelValue(sourceModel);
      }
      this.restoring = true;
      let restoredAny = false;
      let invalid = false;

      for (const key of SELECT_ORDER) {
        let requestedValue = requested[key];
        if (key === "generation" && !requestedValue && requested.year) {
          const candidates = this.applicationsAtStage("model").filter((application) =>
            application.year?.raw === requested.year);
          const ids = [...new Set(candidates.flatMap((application) => application.selector.generations.map((generation) => generation.id)))];
          requestedValue = ids.length === 1 ? ids[0] : "";
          if (!requestedValue) invalid = true;
        }
        if (key === "generation" && requestedValue &&
          ![...this.selects.generation.options].some((option) => option.value === requestedValue)) {
          const candidates = this.applicationsAtStage("model").filter((application) =>
            application.generation === requestedValue &&
            (!requested.year || application.year?.raw === requested.year));
          const ids = [...new Set(candidates.flatMap((application) => application.selector.generations.map((generation) => generation.id)))];
          requestedValue = ids.length === 1 ? ids[0] : "";
          if (!requestedValue) invalid = true;
        }
        if (key === "engineName" && requestedValue &&
          ![...this.selects.engineName.options].some((option) => option.value === requestedValue)) {
          const candidates = this.applicationsAtStage("generation").filter((application) =>
            application.engineName === requestedValue &&
            (!requested.year || application.year?.raw === requested.year));
          const values = [...new Set(candidates.map((application) => this.engineValue(application)))];
          requestedValue = values.length === 1 ? values[0] : "";
          if (!requestedValue) invalid = true;
        }
        if (!requestedValue) {
          if (key === "generation" && this.fields.generation?.hidden) continue;
          if (key === "tcu") continue;
          break;
        }
        const select = this.selects[key];
        const optionExists = [...select.options].some(
          (option) => option.value === requestedValue,
        );
        if (select.disabled || !optionExists) {
          invalid = true;
          break;
        }
        select.value = requestedValue;
        this.handleChange(key, { updateUrl: false });
        restoredAny = true;
      }

      if (this.state.engineName && requested.application) {
        const applications = this.applicationsAtStage("tcu");
        const application = applications.find(
          (candidate) => candidate.id === requested.application,
        );
        if (application) {
          const option = application.options.find(
            (candidate) => candidate.key === requested.option,
          );
          const packageChoice = this.packageChoices(application).find(
            (candidate) => candidate.type === requested.packageType,
          );
          if (option) {
            const optionControl = [
              ...this.querySelectorAll("[data-option-key]"),
            ].find(
              (control) =>
                control.dataset.applicationId === application.id &&
                control.dataset.optionKey === option.key,
            );
            if (optionControl) {
              this.selectOption(application, option, optionControl, {
                updateUrl: false,
              });
            }
          } else if (requested.option) {
            invalid = true;
          }
          if (packageChoice) {
            const packageControl = [
              ...this.querySelectorAll("[data-package-type]"),
            ].find(
              (control) =>
                control.dataset.applicationId === application.id &&
                control.dataset.packageType === packageChoice.type,
            );
            if (packageControl) {
              this.selectPackage(
                application,
                packageChoice,
                packageControl,
                { updateUrl: false },
              );
            }
          } else if (requested.packageType) {
            invalid = true;
          }
        } else {
          invalid = true;
        }
      }

      this.restoring = false;
      this.clearButton.hidden = !this.state.make;
      this.updateUrl();
      if (invalid) {
        this.setStatus(this.labels.restoreIgnored, "ready");
      } else if (restoredAny) {
        this.setStatus(this.labels.restored, "ready");
      }
    }

    updateUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete(URL_KEYS.year);
      for (const key of SELECT_ORDER) {
        const parameter = URL_KEYS[key];
        const value = this.state[key];
        if (value) {
          url.searchParams.set(parameter, value);
        } else {
          url.searchParams.delete(parameter);
        }
      }

      const selectedValues = {
        application: this.selected.applicationId,
        option: this.selected.optionKey,
        packageType: this.selected.packageType,
      };
      for (const [key, value] of Object.entries(selectedValues)) {
        const parameter = URL_KEYS[key];
        if (value) {
          url.searchParams.set(parameter, value);
        } else {
          url.searchParams.delete(parameter);
        }
      }
      window.history.replaceState({}, "", url);
    }
  }

  if (!customElements.get("bh-oem-configurator")) {
    customElements.define(
      "bh-oem-configurator",
      BoostHausOemConfigurator,
    );
  }
})();
